import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getCurrentUserId, getNhDealApplicationId } from "features/authSlice";
import { useGetDashboardItemsQuery } from "pages/dashboard/home/dashboardService";
import { UQUAL_AD_FIRST_SESSION_KEY } from "utilities/Constant";

export interface UqualVisibilityFlags {
  showUqualBanner: boolean;
  showUqualNotification: boolean;
}

export const getUqualVisibilityStorageKey = (userId: string, nhDealApplicationId: string) =>
  `${UQUAL_AD_FIRST_SESSION_KEY}_${userId}_${nhDealApplicationId}`;

const readCachedUqualVisibility = (key: string): UqualVisibilityFlags | null => {
  const raw = sessionStorage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<UqualVisibilityFlags>;
    return {
      showUqualBanner: parsed.showUqualBanner === true,
      showUqualNotification: parsed.showUqualNotification === true,
    };
  } catch {
    return null;
  }
};

interface UseUqualVisibilityFlagsOptions {
  skipQuery?: boolean;
}

export const useUqualVisibilityFlags = (options: UseUqualVisibilityFlagsOptions = {}) => {
  const { skipQuery = false } = options;
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const [flags, setFlags] = useState<UqualVisibilityFlags>({
    showUqualBanner: false,
    showUqualNotification: false,
  });

  const { data: dashboardData } = useGetDashboardItemsQuery(
    { dealApplicationId: nhDealApplicationId, userId: userId ?? "" },
    { skip: skipQuery || !nhDealApplicationId || !userId },
  );

  useEffect(() => {
    if (!userId || !nhDealApplicationId) {
      return;
    }

    const storageKey = getUqualVisibilityStorageKey(userId, nhDealApplicationId);
    const cached = readCachedUqualVisibility(storageKey);
    if (cached) {
      setFlags(cached);
      return;
    }

    if (!dashboardData) {
      return;
    }

    const freshFlags: UqualVisibilityFlags = {
      showUqualBanner: dashboardData.showUqualBanner === true,
      showUqualNotification: dashboardData.showUqualNotification === true,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(freshFlags));
    setFlags(freshFlags);
  }, [userId, nhDealApplicationId, dashboardData?.showUqualBanner, dashboardData?.showUqualNotification]);

  return flags;
};
